import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ShoppingBag, Share2, MessageCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { getProduct } from '../../api/products';
import { useCartStore } from '../../store/cart';
import toast from 'react-hot-toast';
import styles from './ProductPage.module.css';

export default function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const addItem = useCartStore(s => s.addItem);
  const [imgIdx, setImgIdx] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ['product', id],
    queryFn: () => getProduct(id!),
    enabled: !!id,
  });

  if (isLoading) return <div className={styles.loading}>{t('common.loading')}</div>;
  if (!data?.product) return <div className={styles.loading}>Product not found</div>;

  const { product, inventory } = data;
  const images: string[] = product.images || [];
  const stockCount: number = inventory?.stockCount || 0;
  const isTA = i18n.language === 'ta';

  const handleAdd = () => {
    if (!stockCount) return;
    addItem({
      productId: product.id,
      name: product.name,
      price: product.price,
      imageUrl: images[0] || '',
      stockCount,
    });
    toast.success('Added to cart!');
  };

  const shareWhatsApp = () => {
    const text = `Check out this beautiful saree from Sekar Silks: ${product.name} - ₹${product.price.toLocaleString('en-IN')}\n${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div className={styles.page}>
      <Link to="/catalogue" className={styles.back}>← Back to Catalogue</Link>

      <div className={styles.layout}>
        {/* Images */}
        <div className={styles.gallery}>
          <div className={styles.mainImage}>
            {images[imgIdx]
              ? <img src={images[imgIdx]} alt={product.name} />
              : <div className={styles.imagePlaceholder} />
            }
            {images.length > 1 && (
              <>
                <button className={`${styles.navBtn} ${styles.prev}`} onClick={() => setImgIdx(i => Math.max(0, i - 1))}><ChevronLeft /></button>
                <button className={`${styles.navBtn} ${styles.next}`} onClick={() => setImgIdx(i => Math.min(images.length - 1, i + 1))}><ChevronRight /></button>
              </>
            )}
          </div>
          {images.length > 1 && (
            <div className={styles.thumbnails}>
              {images.map((img, i) => (
                <button key={i} className={`${styles.thumb} ${imgIdx === i ? styles.thumbActive : ''}`} onClick={() => setImgIdx(i)}>
                  <img src={img} alt={`${product.name} ${i + 1}`} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className={styles.info}>
          <h1 className={styles.name}>{isTA && product.nameTA ? product.nameTA : product.name}</h1>
          <p className={styles.price}>₹{product.price.toLocaleString('en-IN')}</p>

          <div className={styles.tags}>
            {product.fabric && <span className={styles.tag}>{product.fabric}</span>}
            {product.colour && <span className={styles.tag}>{product.colour}</span>}
            {(product.occasion || []).map((o: string) => <span key={o} className={styles.tag}>{o}</span>)}
          </div>

          <div className={styles.stock}>
            {stockCount === 0
              ? <span className={styles.outOfStock}>{t('product.out_of_stock')}</span>
              : stockCount <= 3
                ? <span className={styles.lowStock}>{t('product.low_stock', { count: stockCount })}</span>
                : <span className={styles.inStock}>{t('product.in_stock')}</span>
            }
          </div>

          <button className={styles.addBtn} onClick={handleAdd} disabled={!stockCount}>
            <ShoppingBag size={20} />
            {stockCount ? t('product.add_to_cart') : t('product.out_of_stock')}
          </button>

          <button className={styles.whatsappBtn} onClick={shareWhatsApp}>
            <MessageCircle size={18} />
            {t('product.share_whatsapp')}
          </button>

          <div className={styles.description}>
            <h3>{t('product.description')}</h3>
            <p>{isTA && product.descriptionTA ? product.descriptionTA : product.description}</p>
          </div>

          <div className={styles.details}>
            {product.fabric && <div className={styles.detail}><span>{t('product.fabric')}</span><span>{product.fabric}</span></div>}
            {product.colour && <div className={styles.detail}><span>{t('product.colour')}</span><span>{product.colour}</span></div>}
          </div>
        </div>
      </div>
    </div>
  );
}
